import { z } from "zod";

const BLOCKED_HOSTNAMES = [
  "localhost",
  "127.0.0.1",
  "[::1]",
  "0.0.0.0",
  "metadata.google.internal",
];

function isPrivateIP(hostname: string): boolean {
  // Block IPv4 private/reserved ranges
  const parts = hostname.split(".");
  if (parts.length === 4 && parts.every((p) => /^\d+$/.test(p))) {
    const first = parseInt(parts[0]!, 10);
    const second = parseInt(parts[1]!, 10);
    if (first === 10) return true; // 10.0.0.0/8
    if (first === 172 && second >= 16 && second <= 31) return true; // 172.16.0.0/12
    if (first === 192 && second === 168) return true; // 192.168.0.0/16
    if (first === 169 && second === 254) return true; // 169.254.0.0/16 (link-local / cloud metadata)
    if (first === 0) return true; // 0.0.0.0/8
  }
  return false;
}

/**
 * Relaxed validator for reading back already-persisted connections.
 * Only checks URL format so legacy/custom-domain rows don't break list/get parsing.
 * Do NOT use this for write paths (create/update) — those must use safeQlikBaseUrl.
 */
const relaxedQlikBaseUrl = z.string().url("baseUrl must be a valid URL");

/**
 * Validates that a URL is a safe Qlik Cloud tenant URL.
 * Blocks SSRF by enforcing HTTPS + allowed domain suffix + no private IPs.
 * Use this for write paths (create/update) only.
 */
const safeQlikBaseUrl = z
  .string()
  .url("baseUrl must be a valid URL")
  .refine((value) => !value.endsWith("/"), {
    message: 'baseUrl must not end with "/" (e.g. https://your-tenant.qlikcloud.com)',
  })
  .refine(
    (value) => {
      try {
        const parsed = new URL(value);
        if (parsed.protocol !== "https:") return false;

        const hostname = parsed.hostname.toLowerCase();

        // Block known dangerous hostnames
        if (BLOCKED_HOSTNAMES.includes(hostname)) return false;

        // Block private IP ranges
        if (isPrivateIP(hostname)) return false;

        // Allow only Qlik Cloud domains
        if (!hostname.endsWith(".qlikcloud.com")) return false;

        // Block URLs with credentials (e.g. https://user@host)
        if (parsed.username || parsed.password) return false;

        return true;
      } catch {
        return false;
      }
    },
    {
      message:
        "baseUrl must be a valid Qlik Cloud HTTPS URL (e.g. https://your-tenant.qlikcloud.com)",
    },
  );

export const qlikAuthMethodSchema = z.enum(["api_key", "oauth_client_credentials"]);
export type QlikAuthMethod = z.infer<typeof qlikAuthMethodSchema>;

export const qlikConnectionStatusSchema = z.enum(["ok", "degraded", "unknown"]);
export type QlikConnectionStatus = z.infer<typeof qlikConnectionStatusSchema>;

export const qlikConnectionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  name: z.string().min(1),
  baseUrl: relaxedQlikBaseUrl,
  authMethod: qlikAuthMethodSchema,
  oauthClientId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  appCount: z.number().nullable().optional(),
  lastSyncAt: z.string().nullable().optional(),
  lastHealthCheckAt: z.string().nullable().optional(),
  lastHealthCheckOk: z.boolean().nullable().optional(),
  lastHealthCheckError: z.string().nullable().optional(),
  status: qlikConnectionStatusSchema.optional(),
});

export type QlikConnection = z.infer<typeof qlikConnectionSchema>;

export const testQlikConnectionResponseSchema = z.object({
  data: z.object({
    ok: z.boolean(),
    error: z.string().nullable(),
    lastHealthCheckAt: z.string(),
  }),
});

export type TestQlikConnectionResponse = z.infer<typeof testQlikConnectionResponseSchema>;

export const createQlikConnectionInputSchema = z.discriminatedUnion("authMethod", [
  z.object({
    authMethod: z.literal("api_key"),
    name: z.string().min(1),
    baseUrl: safeQlikBaseUrl,
    apiKey: z.string().min(1),
  }),
  z.object({
    authMethod: z.literal("oauth_client_credentials"),
    name: z.string().min(1),
    baseUrl: safeQlikBaseUrl,
    clientId: z.string().min(1),
    clientSecret: z.string().min(1),
  }),
]);

export type CreateQlikConnectionInput = z.infer<typeof createQlikConnectionInputSchema>;

export const updateQlikConnectionInputSchema = z.discriminatedUnion("authMethod", [
  z.object({
    authMethod: z.literal("api_key"),
    name: z.string().min(1).optional(),
    baseUrl: safeQlikBaseUrl.optional(),
    apiKey: z.string().min(1).optional(),
  }),
  z.object({
    authMethod: z.literal("oauth_client_credentials"),
    name: z.string().min(1).optional(),
    baseUrl: safeQlikBaseUrl.optional(),
    clientId: z.string().min(1).optional(),
    clientSecret: z.string().min(1).optional(),
  }),
]);

export type UpdateQlikConnectionInput = z.infer<typeof updateQlikConnectionInputSchema>;

export const listQlikConnectionsResponseSchema = z.object({
  data: z.array(qlikConnectionSchema),
});

export type ListQlikConnectionsResponse = z.infer<typeof listQlikConnectionsResponseSchema>;
